import { isValidLocationType } from '@app/enums';
import { SearchCriteria, SearchFilters } from '@app/models';

/**
 * Parse search parameters from URL query params
 * @param searchParams URL search params object
 * @returns Object with parsed searchCriteria and filters
 */
export function parseMapSearchParams(searchParams: URLSearchParams | Record<string, string>): {
    searchCriteria: Partial<SearchCriteria>;
    filters: Partial<SearchFilters>;
} {
    const params =
        searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);

    const defaultGuests = {
        adultsCount: 2,
        childrenCount: 0,
        childrenAges: [],
        guestCount: 2,
    };

    const result: {
        searchCriteria: Partial<SearchCriteria>;
        filters: Partial<SearchFilters>;
    } = {
        searchCriteria: {
            guests: defaultGuests,
        },
        filters: {
            propertyType: [],
            amenities: [],
            provision: [],
            reservationFeatures: [],
        },
    };

    const processArrayFilter = (
        filterKey: keyof Pick<
            SearchFilters,
            'propertyType' | 'amenities' | 'provision' | 'reservationFeatures'
        >,
    ) => {
        const paramKey = `${filterKey}[]`;
        const values = params.getAll(paramKey);
        if (values.length > 0) {
            result.filters[filterKey] = values;
        }
    };

    processArrayFilter('propertyType');
    processArrayFilter('amenities');
    processArrayFilter('provision');
    processArrayFilter('reservationFeatures');

    const childrenAges: number[] = [];

    Array.from(params.entries()).forEach(([key, value]) => {
        if (key.startsWith('childrenAges[')) {
            const age = parseInt(value);
            if (!isNaN(age)) {
                childrenAges.push(age);
            }
            return;
        }
    });

    if (childrenAges.length > 0) {
        if (!result.searchCriteria.guests) {
            result.searchCriteria.guests = defaultGuests;
        }
        result.searchCriteria.guests.childrenAges = childrenAges;
    }

    const location = params.get('location');
    if (location) {
        result.searchCriteria.location = location;
    }

    const checkInDate = params.get('checkInDate');
    if (checkInDate) {
        result.searchCriteria.checkInDate = checkInDate;
    }

    const checkOutDate = params.get('checkOutDate');
    if (checkOutDate) {
        result.searchCriteria.checkOutDate = checkOutDate;
    }

    const adultsCount = params.get('adultsCount');
    if (adultsCount) {
        const count = parseInt(adultsCount);
        if (!isNaN(count)) {
            if (!result.searchCriteria.guests) {
                result.searchCriteria.guests = defaultGuests;
            }
            result.searchCriteria.guests.adultsCount = count;
        }
    }

    const childrenCount = params.get('childrenCount');
    if (childrenCount) {
        const count = parseInt(childrenCount);
        if (!isNaN(count)) {
            if (!result.searchCriteria.guests) {
                result.searchCriteria.guests = defaultGuests;
            }
            result.searchCriteria.guests.childrenCount = count;
        }
    }

    const guestCount = params.get('guestCount');
    if (guestCount) {
        const count = parseInt(guestCount);
        if (!isNaN(count)) {
            if (!result.searchCriteria.guests) {
                result.searchCriteria.guests = defaultGuests;
            }
            result.searchCriteria.guests.guestCount = count;
        }
    }

    const geoId = params.get('geoId');
    if (geoId) {
        result.searchCriteria.geoId = geoId;
    }

    const type = params.get('type');
    if (type && isValidLocationType(type)) {
        result.searchCriteria.locationType = type;
    }

    const rating = params.get('rating');
    if (rating) {
        const ratingValue = parseFloat(rating);
        if (!isNaN(ratingValue)) {
            result.filters.rating = ratingValue;
        }
    }

    const priceTo = params.get('priceTo');
    if (priceTo) {
        const priceToValue = parseInt(priceTo);
        if (!isNaN(priceToValue)) {
            result.filters.priceTo = priceToValue;
        }
    }

    const priceFrom = params.get('priceFrom');
    if (priceFrom) {
        const priceFromValue = parseInt(priceFrom);
        if (!isNaN(priceFromValue)) {
            result.filters.priceFrom = priceFromValue;
        }
    }

    // Handle legacy parameters for navigating back
    const sort = params.get('sort');
    if (sort) {
        result.filters.sort = sort;
    }

    const onlineBooking = params.get('onlineBooking');
    if (onlineBooking) {
        result.filters.onlineBooking = onlineBooking;
    }

    return result;
}

/**
 * Convert search state to URL parameters
 * @param searchCriteria Search criteria object
 * @param filters Filters object
 * @returns URLSearchParams object
 */
export function mapSearchStateToUrlParams(
    searchCriteria: Partial<SearchCriteria>,
    filters: Partial<SearchFilters>,
): URLSearchParams {
    const params = new URLSearchParams();
    if (searchCriteria.location) {
        params.set('location', searchCriteria.location);
    }

    if (searchCriteria.checkInDate) {
        // Use the date string directly or convert to YYYY-MM-DD format if it's an ISO string
        const formattedDate = searchCriteria.checkInDate.includes('T')
            ? searchCriteria.checkInDate.split('T')[0]
            : searchCriteria.checkInDate;
        params.set('checkInDate', formattedDate);
    }

    if (searchCriteria.checkOutDate) {
        // Use the date string directly or convert to YYYY-MM-DD format if it's an ISO string
        const formattedDate = searchCriteria.checkOutDate.includes('T')
            ? searchCriteria.checkOutDate.split('T')[0]
            : searchCriteria.checkOutDate;
        params.set('checkOutDate', formattedDate);
    }

    if (searchCriteria.guests) {
        const { adultsCount, childrenCount, childrenAges, guestCount } = searchCriteria.guests;

        if (adultsCount !== undefined) {
            params.set('adultsCount', adultsCount.toString());
        }

        if (childrenCount !== undefined) {
            params.set('childrenCount', childrenCount.toString());
        }

        if (guestCount !== undefined) {
            params.set('guestCount', guestCount.toString());
        }

        if (childrenAges && childrenAges.length > 0) {
            childrenAges.forEach((age, index) => {
                params.set(`childrenAges[${index}]`, age.toString());
            });
        }
    }

    if (searchCriteria.geoId) {
        params.set('geoId', searchCriteria.geoId);
    }

    if (searchCriteria.locationType) {
        params.set('type', searchCriteria.locationType);
    }

    // Handle legacy parameters for navigating back
    if (filters.sort) {
        params.set('sort', filters.sort);
    }

    if (filters.onlineBooking) {
        params.set('onlineBooking', filters.onlineBooking);
    }

    const appendFilter = (
        filterKey: keyof Pick<SearchFilters, 'rating' | 'priceFrom' | 'priceTo'>,
    ) => {
        const value = filters[filterKey];
        if (value !== undefined && value !== null) {
            params.set(filterKey, value.toString());
        }
    };

    const appendArrayFilter = (
        filterKey: keyof Pick<
            SearchFilters,
            'propertyType' | 'amenities' | 'provision' | 'reservationFeatures'
        >,
    ) => {
        const values = filters[filterKey];
        if (values && Array.isArray(values) && values.length > 0) {
            values.forEach(value => {
                params.append(`${filterKey}[]`, value);
            });
        }
    };

    appendFilter('rating');
    appendFilter('priceFrom');
    appendFilter('priceTo');

    appendArrayFilter('propertyType');
    appendArrayFilter('amenities');
    appendArrayFilter('provision');
    appendArrayFilter('reservationFeatures');

    return params;
}
